import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Session Token Storage - SECURE VERSION
 * 
 * Primary auth: httpOnly cookies (set by backend, not accessible to JS - XSS safe)
 * Fallback for legacy/migration: Bearer token in localStorage (will be phased out)
 * 
 * The backend sets httpOnly cookies on login/register/session endpoints.
 * axios with withCredentials:true automatically sends cookies with requests.
 */
const TOKEN_KEY = 'session_token';
const REMEMBER_KEY = 'remember_me';

// Token getter - checks both session and local storage
const getSessionToken = () => {
  try {
    const rememberMe = localStorage.getItem(REMEMBER_KEY) === 'true';
    if (rememberMe) {
      return localStorage.getItem(TOKEN_KEY);
    }
    return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    return null;
  }
};

// Store session token (backup for when cookies don't work)
const storeSessionToken = (token, rememberMe = false) => {
  try {
    if (rememberMe) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(REMEMBER_KEY, 'true');
    } else {
      sessionStorage.setItem(TOKEN_KEY, token);
      localStorage.removeItem(REMEMBER_KEY);
    }
  } catch (error) {
    // Storage not available
  }
};

// Clear legacy tokens on logout
export const clearLegacyTokens = () => {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REMEMBER_KEY);
  } catch (error) {
    // Storage not available
  }
};

// Check if user is remembered (for auto-login UI hints)
export const isRemembered = () => {
  try {
    return localStorage.getItem(REMEMBER_KEY) === 'true';
  } catch {
    return false;
  }
};

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
  // Enable sending cookies with cross-origin requests (for httpOnly cookie auth)
  withCredentials: true,
});

// Add Authorization header for legacy token support (migration period)
api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', error.response?.data || error.message);
    }
    return Promise.reject(error);
  }
);

// Auth APIs (Google OAuth + Email/Password)
// Note: httpOnly cookies are automatically set by backend on successful auth
// No client-side token storage needed - cookies are sent automatically with withCredentials:true
export const authAPI = {
  // Google OAuth
  createSession: async (sessionId) => {
    const response = await api.post("/auth/session", { session_id: sessionId });
    // Cookie is set by backend, no need to store token
    return response;
  },
  
  // Email/Password Auth
  register: async (data) => {
    const response = await api.post("/auth/register", data);
    // Store token as backup in case cookies don't work (cross-origin, etc.)
    if (response.data?.session_token) {
      storeSessionToken(response.data.session_token, false);
    }
    return response;
  },
  login: async (data) => {
    const response = await api.post("/auth/login", data);
    // Store token as backup in case cookies don't work (cross-origin, etc.)
    if (response.data?.session_token) {
      storeSessionToken(response.data.session_token, data.remember_me);
    }
    return response;
  },
  forgotPassword: (data) => api.post("/auth/forgot-password", data),
  resetPassword: (data) => api.post("/auth/reset-password", data),
  
  // Common
  getMe: () => api.get("/auth/me"),
  setUserType: (data) => api.post("/auth/set-user-type", data),
  switchRole: () => api.post("/auth/switch-role"),
  logout: async () => {
    const response = await api.post("/auth/logout");
    clearLegacyTokens(); // Clear any legacy tokens from localStorage
    return response;
  },
};

// Cargo Listing APIs
export const cargoAPI = {
  create: (data) => api.post("/listings/cargo", data),
  getAll: (params) => api.get("/listings/cargo", { params }),
  getById: (id) => api.get(`/listings/cargo/${id}`),
  getMine: () => api.get("/listings/cargo/my/listings"),
  delete: (id) => api.delete(`/listings/cargo/${id}`),
};

// Jet Listing APIs
export const jetAPI = {
  create: (data) => api.post("/listings/jet", data),
  getAll: (params) => api.get("/listings/jet", { params }),
  getById: (id) => api.get(`/listings/jet/${id}`),
  getMine: () => api.get("/listings/jet/my/listings"),
  delete: (id) => api.delete(`/listings/jet/${id}`),
};

// Booking APIs
export const bookingAPI = {
  create: (data) => api.post("/bookings", data),
  getMyBookings: () => api.get("/bookings/my"),
  getPartnerBookings: () => api.get("/bookings/partner"),
  cancelBooking: (bookingId) => api.post(`/bookings/${bookingId}/cancel`),
  acceptBooking: (bookingId) => api.post(`/bookings/${bookingId}/accept`),
  declineBooking: (bookingId) => api.post(`/bookings/${bookingId}/decline`),
};

// Payment APIs
export const paymentAPI = {
  createOrder: (bookingId) => api.post("/payments/create-order", { booking_id: bookingId }),
  verifyPayment: (data) => api.post("/payments/verify", data),
};

// Message APIs
export const messageAPI = {
  send: (bookingId, content) => api.post("/messages", { booking_id: bookingId, content }),
  getMessages: (bookingId) => api.get(`/messages/${bookingId}`),
};

// Stats API
export const statsAPI = {
  getDashboard: () => api.get("/stats/dashboard"),
};

// Notification APIs
export const notificationAPI = {
  getAll: () => api.get("/notifications"),
  getUnreadCount: () => api.get("/notifications/unread-count"),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post("/notifications/read-all"),
};

// Tracking APIs
export const trackingAPI = {
  getInfo: (deliveryId) => api.get(`/tracking/${deliveryId}`),
  updateLocation: (deliveryId, location) => api.post(`/tracking/${deliveryId}/location`, location),
};

// Partner Availability APIs
export const partnerAPI = {
  goOnline: (location) => api.post("/partner/go-online", location),
  goOffline: () => api.post("/partner/go-offline"),
  getAvailability: () => api.get("/partner/availability"),
  updateLocation: (location) => api.post("/partner/update-location", location),
  getNearbyDeliveries: () => api.get("/partner/nearby-deliveries"),
};

// Reviews API
export const reviewAPI = {
  create: (data) => api.post("/reviews", data),
  createDeliveryReview: (data) => api.post("/reviews/delivery", data),
  getForService: (serviceType, referenceId) => api.get(`/reviews/${serviceType}/${referenceId}`),
  getMyReviews: () => api.get("/reviews/my"),
  getPartnerReviews: () => api.get("/reviews/partner"),
};

// Negotiations API (Price Negotiation)
export const negotiationAPI = {
  create: (data) => api.post("/negotiations", data),
  getAll: () => api.get("/negotiations"),
  getOne: (negotiationId) => api.get(`/negotiations/${negotiationId}`),
  counter: (negotiationId, data) => api.post(`/negotiations/${negotiationId}/counter`, data),
  accept: (negotiationId) => api.post(`/negotiations/${negotiationId}/accept`),
  reject: (negotiationId) => api.post(`/negotiations/${negotiationId}/reject`),
  getForListing: (listingId) => api.get(`/negotiations/listing/${listingId}`),
};

export default api;
